import { useEffect, useMemo, useRef, useState } from 'react';
import { edgeSpeed, moved } from '../../lib/dragList';

/**
 * Drag to reorder, one way everywhere in the back office (Back office sweep, Oct 2026), as
 * learnt on the till's pad: the row you're holding leaves a gap where it will land and the
 * others slide along; the page scrolls when you hold it near the top or bottom; and a drop
 * anywhere in the list lands where the gap was. Structure and Kitchen stations both use it.
 *
 *   const drag = useDragList(sections, (s) => s.id, (keys) => save(keys));
 *   <div {...drag.listProps}>
 *     {drag.rows.map((s, i) => (
 *       <div key={s.id} {...drag.rowProps(s, i)}><span {...drag.handleProps(s)}>⋮⋮</span>…</div>
 *     ))}
 *   </div>
 *
 * `onDrop(keys)` gets the new order, and only when it changed.
 */
export default function useDragList(list, keyOf, onDrop) {
  const [held, setHeld] = useState(null);
  const [at, setAt] = useState(null);
  const pointerY = useRef(null);
  const rows = useMemo(() => moved(list, keyOf, held, at), [list, held, at]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll while holding near the edge of the screen.
  useEffect(() => {
    if (held == null) return undefined;
    let frame;
    const track = (e) => { pointerY.current = e.clientY; };
    const tick = () => {
      const speed = pointerY.current == null ? 0 : edgeSpeed(pointerY.current, window.innerHeight);
      if (speed) (document.scrollingElement || document.documentElement).scrollBy(0, speed);
      frame = requestAnimationFrame(tick);
    };
    document.addEventListener('dragover', track);
    frame = requestAnimationFrame(tick);
    return () => { document.removeEventListener('dragover', track); cancelAnimationFrame(frame); pointerY.current = null; };
  }, [held]);

  const finish = () => {
    const order = rows;
    setHeld(null);
    setAt(null);
    if (order !== list) onDrop(order.map(keyOf));
  };

  return {
    rows,
    held,
    isHeld: (item) => held != null && keyOf(item) === held,
    handleProps: (item) => ({
      draggable: true,
      onDragStart: (e) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(keyOf(item)));
        setHeld(keyOf(item));
        setAt(list.findIndex((x) => keyOf(x) === keyOf(item)));
      },
      onDragEnd: () => { setHeld(null); setAt(null); },
    }),
    rowProps: (item, index) => (held == null ? {} : {
      onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; if (index !== at) setAt(index); },
      onDrop: (e) => { e.preventDefault(); e.stopPropagation(); finish(); },
    }),
    // The list as a whole takes the drop too, so letting go in a gap between rows still lands.
    listProps: held == null ? {} : {
      onDragOver: (e) => { e.preventDefault(); },
      onDrop: (e) => { e.preventDefault(); finish(); },
    },
  };
}
